// Miejsca — zapisane punkty na mapie pod własną nazwą, w listach miejsc (kolor pinezek),
// ulubione (serduszko) i przypięte (kafelki „Nawiguj"). Mapa to OpenStreetMap, a nawigacja
// idzie przez link do Google Maps, jak przy miejscu wydarzenia. Lokalizacja to tylko
// bieżąca pozycja telefonu („Tu jestem"), nigdy zapisane miejsce.
import { computed, ref } from 'vue';
import { useApi } from '~/composables/useApi.ts';
import { intlLocale } from '@/composables/useLanguage.js';
import { t } from '@/i18n/index.js';

// Wspólny stan na cały moduł (i wybór miejsca w wydarzeniu).
const places = ref([]);
const lists = ref([]);
const settings = ref({ default_view: 'map' });
const listeners = new Set();

export const MAX_PINNED = 6;

// Paleta list miejsc — klucze jak w API (PlaceList::COLORS). Nazwa koloru to getter,
// bo język zmienia się w trakcie działania aplikacji.
const paletteColor = (value, hex) => ({ value, hex, get label() { return t(`places.colors.${value}`); } });
export const PLACE_COLORS = [
    paletteColor('red', '#C2523F'),
    paletteColor('orange', '#D9793A'),
    paletteColor('amber', '#C99A2E'),
    paletteColor('green', '#4F8A5B'),
    paletteColor('teal', '#3F8A8C'),
    paletteColor('blue', '#3E6FB0'),
    paletteColor('indigo', '#5A5BAE'),
    paletteColor('purple', '#8A5AA8'),
    paletteColor('pink', '#C2588A'),
    paletteColor('gray', '#7A746B'),
];
export const NO_LIST_HEX = '#211E1A';
export const colorHex = (color) => PLACE_COLORS.find(c => c.value === color)?.hex ?? NO_LIST_HEX;

// Domyślny widok mapy bez miejsc i bez lokalizacji: środek Polski.
export const DEFAULT_CENTER = { lat: 52.07, lng: 19.48, zoom: 6 };

const fixed = (n) => Number(n).toFixed(6).replace(/\.?0+$/, '');

// Nawigacja w Google Maps prosto do punktu.
export const navigateUrl = (place) =>
    `https://www.google.com/maps/dir/?api=1&destination=${fixed(place.latitude)},${fixed(place.longitude)}`;

// Tekst wstawiany jako miejsce wydarzenia: nazwa i link z punktem — kopia, którą
// useMapsLocation zamienia potem na etykietę i nawigację.
export const eventLocationText = (place) =>
    `${place.name} https://www.google.com/maps/search/?api=1&query=${fixed(place.latitude)},${fixed(place.longitude)}`;

// Odległość w km (wzór haversine).
export const distanceKm = (a, b) => {
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b.latitude - a.latitude);
    const dLng = rad(b.longitude - a.longitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
};
export const distanceText = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toLocaleString(intlLocale(), { maximumFractionDigits: km < 10 ? 1 : 0 })} km`);

// Lokalizacja telefonu — { latitude, longitude } albo błąd z komunikatem dla użytkownika.
export const currentLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error(t('places.location.unsupported'))); return; }
    navigator.geolocation.getCurrentPosition(
        (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
        (e) => reject(new Error(e.code === 1 ? t('places.location.denied') : t('places.location.failed'))),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
});

// Po każdym zapisie (np. Pierwsze kroki odświeżają stan).
export const onMiejscaChange = (fn) => listeners.add(fn);
const changed = () => listeners.forEach(fn => fn());

export function useMiejsca() {
    const api = useApi();

    const loadPlaces = async () => { places.value = await api.get('/places'); };
    const loadLists = async () => { lists.value = await api.get('/place-lists'); };
    const loadSettings = async () => { settings.value = await api.get('/places/settings'); };
    const load = () => Promise.all([loadPlaces(), loadLists()]);

    const listOf = (place) => lists.value.find(l => l.uuid === place.list) ?? null;
    const pinned = computed(() => places.value.filter(p => p.pinned));
    const pinnedFull = computed(() => pinned.value.length >= MAX_PINNED);

    const body = (p) => ({
        name: p.name,
        latitude: p.latitude,
        longitude: p.longitude,
        address: p.address || null,
        note: p.note || null,
        list: p.list || null,
        favorite: !!p.favorite,
        pinned: !!p.pinned,
    });

    const savePlace = async (place, uuid = null) => {
        const saved = uuid ? await api.put(`/places/${uuid}`, body(place)) : await api.post('/places', body(place));
        await Promise.all([loadPlaces(), loadLists()]);
        changed();
        return saved;
    };
    // Serduszko i pinezka na liście — bez otwierania formularza.
    const toggle = async (place, key) => {
        const saved = await api.put(`/places/${place.uuid}`, body({ ...place, [key]: !place[key] }));
        places.value = places.value.map(p => (p.uuid === saved.uuid ? saved : p));
        changed();
        return saved;
    };
    const removePlace = async (uuid) => {
        await api.delete(`/places/${uuid}`);
        await Promise.all([loadPlaces(), loadLists()]);
        changed();
    };
    const orderPlaces = async (uuids) => {
        const rank = new Map(uuids.map((u, i) => [u, i]));
        places.value = places.value.map(p => (rank.has(p.uuid) ? { ...p, order: rank.get(p.uuid) } : p));
        await api.put('/places/order', { uuids });
    };

    const saveList = async (list, uuid = null) => {
        const payload = { name: list.name, color: list.color };
        const saved = uuid ? await api.put(`/place-lists/${uuid}`, payload) : await api.post('/place-lists', payload);
        await loadLists();
        changed();
        return saved;
    };
    const removeList = async (uuid) => {
        await api.delete(`/place-lists/${uuid}`);
        await load();
        changed();
    };
    const orderLists = async (uuids) => {
        const rank = new Map(uuids.map((u, i) => [u, i]));
        lists.value = [...lists.value].sort((a, b) => rank.get(a.uuid) - rank.get(b.uuid));
        await api.put('/place-lists/order', { uuids });
    };

    const saveSettings = async (next) => {
        settings.value = await api.put('/places/settings', { ...settings.value, ...next });
    };

    // OpenStreetMap przez API (Nominatim: limit zapytań, więc strona czeka z szukaniem).
    const search = (q, near = null) => api.get('/places/search', { query: { q, ...(near ? { lat: near.latitude, lng: near.longitude } : {}) } });
    const reverse = async (latitude, longitude) => {
        try { return await api.get('/places/reverse', { query: { lat: latitude, lng: longitude } }); } catch { return null; }
    };
    const fromLink = (text) => api.post('/places/from-link', { text });

    return {
        api, places, lists, settings, pinned, pinnedFull,
        load, loadPlaces, loadLists, loadSettings, listOf,
        savePlace, toggle, removePlace, orderPlaces,
        saveList, removeList, orderLists, saveSettings,
        search, reverse, fromLink,
    };
}
